import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

const ADD_ONE_SELECTOR = 'a[href="#add-one"], a[href$="#add-one"]';

/**
 * @returns {string | undefined}
 */
function getVariantId() {
  const fromPacks = document.querySelector('[data-add-one-variant]')?.getAttribute('data-add-one-variant');
  if (fromPacks) return fromPacks;

  const fromForm = document.querySelector('product-form-component input[name="id"]');
  if (fromForm instanceof HTMLInputElement && fromForm.value) return fromForm.value;

  return undefined;
}

/**
 * @returns {string}
 */
function getCartSectionIds() {
  const sectionIds = [];
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.push(item.dataset.sectionId);
    }
  });
  return sectionIds.join(',');
}

/**
 * @returns {Promise<any>}
 */
function fetchCart() {
  return fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((response) => {
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  });
}

/**
 * @param {HTMLAnchorElement} link
 * @param {boolean} busy
 */
function setBusy(link, busy) {
  link.setAttribute('aria-busy', busy ? 'true' : 'false');
  if (busy) {
    link.setAttribute('aria-disabled', 'true');
  } else {
    link.removeAttribute('aria-disabled');
  }
}

/**
 * @param {HTMLAnchorElement} link
 */
async function addOneToCart(link) {
  const variantId = getVariantId();
  if (!variantId) {
    console.warn('[add-one] No product variant found');
    return;
  }

  if (link.getAttribute('aria-busy') === 'true') return;

  setBusy(link, true);

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', '1');

  const sectionIds = getCartSectionIds();
  if (sectionIds) formData.append('sections', sectionIds);

  const deferredEventPromise = CartLinesUpdateEvent.createPromise();

  link.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [
        {
          merchandiseId: variantId,
          quantity: 1,
        },
      ],
      promise: deferredEventPromise.promise,
    })
  );

  const fetchCfg = fetchConfig('javascript', { body: formData });

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      credentials: 'same-origin',
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    });
    const data = await response.json();

    if (data.status) {
      const ajaxCart = await fetchCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          didError: true,
          items: ajaxCart.items,
          source: 'add-one',
          itemCount: 1,
        },
      });
      console.warn('[add-one]', data.message || data.description || 'Add to cart failed');
      return;
    }

    const ajaxCart = await fetchCart();
    deferredEventPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'add-one',
        itemCount: 1,
        productId: ajaxCart.items?.[0]?.product_id,
        sections: data.sections,
        didError: false,
      },
    });
  } catch (error) {
    console.error('[add-one]', error);
    deferredEventPromise.reject(error);
  } finally {
    setBusy(link, false);
  }
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const link = target.closest(ADD_ONE_SELECTOR);
  if (!(link instanceof HTMLAnchorElement)) return;

  event.preventDefault();
  addOneToCart(link);
});
